<template>
  <AppModal @click="() => emit('close')">
    <div class="p-4 leading-6">
      <h2 class="text-base md:text-lg font-semibold">
        {{ t('common.share') }}
      </h2>
      <span class="text-sm md:text-base mb-4">{{ title }}</span>
      <div
        class="a2a_kit a2a_kit_size_32 a2a_default_style flex gap-4 justify-center mt-4"
        :data-a2a-url="shareUrl"
        :data-a2a-title="shareTitle"
      >
        <a
          class="a2a_button_facebook hover:opacity-80 transition-opacity"
          title="Share on Facebook"
        ></a>
        <a
          class="a2a_button_whatsapp hover:opacity-80 transition-opacity"
          title="Share on WhatsApp"
        ></a>
        <a
          class="a2a_button_threads hover:opacity-80 transition-opacity"
          title="Share on Threads"
        ></a>
        <a
          class="a2a_button_x hover:opacity-80 transition-opacity"
          title="Share on X"
        ></a>
        <a
          class="a2a_button_email hover:opacity-80 transition-opacity"
          title="Share via Email"
        ></a>
      </div>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { useI18n } from '#imports';

interface Props {
  url: string;
  title: string;
}

const { t } = useI18n();

const AppModal = defineAsyncComponent(
  () => import('@/async-components/App/Modal.vue')
);

const props = withDefaults(defineProps<Props>(), {
  url: typeof window !== 'undefined' ? window.location.href : '',
  title: 'PFS Realty'
});

const shareUrl = computed(
  () => props.url || (typeof window !== 'undefined' ? window.location.href : '')
);
const shareTitle = computed(() => props.title);

const emit = defineEmits(['close']);

declare global {
  interface Window {
    a2a_config: {
      locale?: string;
    };
    a2a: {
      init_all: () => void;
    };
  }
}

const initializeA2A = async () => {
  console.log('initializeA2A');
  try {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line camelcase
      window.a2a_config = window.a2a_config || {};
      // eslint-disable-next-line camelcase
      window.a2a_config.locale = 'es';

      const existingScripts = document.querySelectorAll(
        'script[src*="addtoany"]'
      );

      if (!existingScripts.length) {
        const script = document.createElement('script');
        script.defer = true;
        script.src = 'https://static.addtoany.com/menu/page.js';
        document.head.appendChild(script);

        // Create a promise to wait for script to load
        await new Promise(resolve => {
          script.onload = () => {
            console.log('AddToAny script loaded');
            resolve(true);
          };
          script.onerror = error => {
            console.error('Error loading AddToAny script:', error);
            resolve(false);
          };
        });
      }

      // Wait a bit longer before initializing
      await new Promise(resolve => setTimeout(resolve, 500));

      if (window.a2a) {
        console.log('Initializing AddToAny');
        window.a2a.init_all();
      } else {
        console.error('AddToAny not available after script load');
      }
    }
  } catch (error) {
    console.error('Error initializing AddToAny:', error);
  }
};

onMounted(() => {
  // Use a client-only directive to ensure this only runs in the browser
  if (process.client) {
    // Give the component time to fully render before initializing AddToAny
    setTimeout(() => {
      initializeA2A();
    }, 300);
  } else {
    console.warn('Share dialog not initialized: Not in client');
  }
});
</script>

<style scoped>
/* AddToAny will inject its own styles */
</style>
